import apiService from './apiService';

class FolderD3Service {
  // Customer Data Folder Management
  async createFolder(folderConfig) {
    try {
      // Extract processing options from the nested structure
      const processingOptions = folderConfig.processingOptions || {};
      
      // Transform the config to match API structure for D3
      const apiPayload = {
        name: folderConfig.name,
        description: folderConfig.description,
        vectorStore: folderConfig.vectorStore || 'PGVector',
        embeddingModel: folderConfig.embeddingModel || 'text-embedding-3-large',
        useBatchMode: folderConfig.useBatchMode !== undefined ? folderConfig.useBatchMode : true,
        folder_type: 'd3_knowledge_base', // Mark as D3 folder
        processingOptions: {
          chunkingStrategy: processingOptions.chunkingStrategy || 'semantic',
          dataSize: processingOptions.dataSize || 'medium',
          chunkSize: processingOptions.chunkSize || 1000,
          overlap: processingOptions.overlap || 200,
          similarityThreshold: processingOptions.similarityThreshold || 0.5,
          agenticModel: processingOptions.agenticModel || 'gpt-4o-mini',
          maxChunkSize: processingOptions.maxChunkSize || 2000,
          splitOnPages: processingOptions.splitOnPages !== undefined ? processingOptions.splitOnPages : true,
          skipHeader: processingOptions.skipHeader !== undefined ? processingOptions.skipHeader : false,
          cleanRows: processingOptions.cleanRows !== undefined ? processingOptions.cleanRows : true,
          searchType: processingOptions.searchType || 'hybrid',
          knowledgeBaseType: 'D3' // D3-specific processing
        }
      };

      // Optional: create as a nested subfolder under an existing folder.
      if (folderConfig.parentFolderId !== undefined && folderConfig.parentFolderId !== null) {
        apiPayload.parentFolderId = folderConfig.parentFolderId;
      }

      console.log('Sending D3 folder config to API:', apiPayload);
      const result = await apiService.createKnowledgeBaseFolderD3(apiPayload);
      return result;
    } catch (error) {
      console.error('Error creating D3 knowledge base folder:', error);
      throw error;
    }
  }

  async getFolders(folderType = 'd3_knowledge_base') {
    try {
      const result = await apiService.getKnowledgeBaseFoldersD3();
      return result;
    } catch (error) {
      console.error('Error fetching D3 knowledge base folders:', error);
      throw error;
    }
  }

  // Nested folder tree (roots with `children`). Mirrored Drive folders carry
  // `sourceExternalId`; nesting is expressed via `parentFolderId`.
  async getFolderTree() {
    try {
      const result = await apiService.getKnowledgeBaseFolderTreeD3();
      return result;
    } catch (error) {
      console.error('Error fetching D3 knowledge base folder tree:', error);
      throw error;
    }
  }

  async getFolder(folderId) {
    try {
      const result = await apiService.getKnowledgeBaseFolderD3(folderId);
      return result;
    } catch (error) {
      console.error('Error fetching D3 knowledge base folder:', error);
      throw error;
    }
  }

  async getFolderWithFiles(folderId) {
    try {
      const result = await apiService.getKnowledgeBaseFolderWithFilesD3(folderId);
      return result;
    } catch (error) {
      console.error('Error fetching D3 folder with files:', error);
      throw error;
    }
  }

  async getFolderConfiguration(folderId) {
    try {
      const result = await apiService.getKnowledgeBaseFolderConfigurationD3(folderId);
      return result;
    } catch (error) {
      console.error('Error fetching D3 folder configuration:', error);
      throw error;
    }
  }

  async updateFolder(folderId, folderConfig) {
    try {
      // Extract processing options from the nested structure
      const processingOptions = folderConfig.processingOptions || {};
      
      // Transform the config to match API structure for D3
      const apiPayload = {
        name: folderConfig.name,
        description: folderConfig.description,
        folder_type: 'd3_knowledge_base', // Mark as D3 folder
        processingOptions: {
          chunkingStrategy: processingOptions.chunkingStrategy || 'semantic',
          dataSize: processingOptions.dataSize || 'medium',
          chunkSize: processingOptions.chunkSize || 1000,
          overlap: processingOptions.overlap || 200,
          similarityThreshold: processingOptions.similarityThreshold || 0.5,
          agenticModel: processingOptions.agenticModel || 'gpt-4o-mini',
          maxChunkSize: processingOptions.maxChunkSize || 2000,
          splitOnPages: processingOptions.splitOnPages !== undefined ? processingOptions.splitOnPages : true,
          skipHeader: processingOptions.skipHeader !== undefined ? processingOptions.skipHeader : false,
          cleanRows: processingOptions.cleanRows !== undefined ? processingOptions.cleanRows : true,
          searchType: processingOptions.searchType || 'hybrid',
          knowledgeBaseType: 'D3' // D3-specific processing
        }
      };

      console.log('Sending D3 update config to API:', apiPayload);
      const result = await apiService.updateKnowledgeBaseFolderD3(folderId, apiPayload);
      return result;
    } catch (error) {
      console.error('Error updating D3 knowledge base folder:', error);
      throw error;
    }
  }

  async deleteFolder(folderId) {
    try {
      const result = await apiService.deleteKnowledgeBaseFolderD3(folderId);
      return result;
    } catch (error) {
      console.error('Error deleting D3 knowledge base folder:', error);
      throw error;
    }
  }

  // Auto-detect tags for D3 folder naming
  autoDetectTags(fileName) {
    const name = fileName.toLowerCase();
    
    // Define tag patterns for D3
    const tagPatterns = {
      'SOP': ['sop', 'standard', 'procedure', 'policy'],
      'POLICY': ['policy', 'policies', 'guideline', 'guidelines'],
      'BP': ['best', 'practice', 'bp', 'template'],
      'TEMPLATE': ['template', 'templates', 'form', 'forms'],
      'ADV': ['advice', 'advisory', 'recommendation', 'guide'],
      'TRAINING': ['training', 'tutorial', 'course', 'learning'],
      'CALL_LOG': ['call', 'log', 'transcript', 'recording'],
      'ORG': ['org', 'organization', 'company', 'corporate'],
      'TRADE_SECRET': ['secret', 'confidential', 'proprietary', 'internal'],
      'DELIVERY': ['delivery', 'shipping', 'logistics', 'fulfillment'],
      'PERSONA': ['persona', 'profile', 'customer', 'user'],
      'TP': ['tp', 'third', 'party', 'external'],
      'TLD': ['tld', 'top', 'level', 'domain'],
      'NICHE': ['niche', 'specialized', 'expert', 'technical'],
      'DNS': ['dns', 'domain', 'name', 'system']
    };

    // Find matching patterns
    const matchedTags = [];
    for (const [tag, patterns] of Object.entries(tagPatterns)) {
      if (patterns.some(pattern => name.includes(pattern))) {
        matchedTags.push(tag);
      }
    }

    // Return primary and secondary tags
    return {
      primaryTag: matchedTags[0] || null,
      secondaryTags: matchedTags.slice(1)
    };
  }

  // D3-specific processing rules
  getD3ProcessingRules(primaryTag) {
    const processingRules = {
      'SOP': { vectorized: true, summarized: false, preserved: true, note: 'Vectorized for search and preserved for compliance' },
      'POLICY': { vectorized: true, summarized: false, preserved: true, note: 'Vectorized for search and preserved for compliance' },
      'TRADE_SECRET': { vectorized: true, summarized: false, preserved: true, note: 'Vectorized for search and preserved for compliance' },
      'BP': { vectorized: true, summarized: false, preserved: true, note: 'Vectorized for search but preserved for reference' },
      'TEMPLATE': { vectorized: true, summarized: false, preserved: true, note: 'Vectorized for search but preserved for reference' },
      'ADV': { vectorized: true, summarized: false, preserved: true, note: 'Vectorized for search but preserved for reference' },
      'TRAINING': { vectorized: true, summarized: true, preserved: false, note: 'Vectorized and summarized for efficient learning' },
      'CALL_LOG': { vectorized: true, summarized: true, preserved: false, note: 'Vectorized and summarized for efficient learning' },
      'TP': { vectorized: true, summarized: false, preserved: false, note: 'Vectorized for analysis, preservation varies by type' },
      'NICHE': { vectorized: true, summarized: false, preserved: false, note: 'Vectorized for analysis, preservation varies by type' },
      'ORG': { vectorized: true, summarized: false, preserved: true, note: 'Organizational content vectorized and preserved' },
      'PERSONA': { vectorized: true, summarized: false, preserved: true, note: 'Vectorized for analysis, selectively preserved' },
      'DNS': { vectorized: true, summarized: false, preserved: true, note: 'DNS content vectorized and preserved' },
      'DELIVERY': { vectorized: true, summarized: false, preserved: true, note: 'Delivery content vectorized and preserved' },
      'TLD': { vectorized: true, summarized: false, preserved: true, note: 'Vectorized for analysis, selectively preserved' },
    };

    return processingRules[primaryTag] || { vectorized: true, summarized: false, preserved: true, note: 'Vectorized for search by default' };
  }

  // Create D3-specific folder configuration
  createD3FolderConfig(config) {
    return {
      ...config,
      folder_type: 'd3_knowledge_base',
      knowledgeBaseType: 'D3',
      processingRules: '4AY' // 4AY rules for D3
    };
  }
}

const folderD3Service = new FolderD3Service();
export default folderD3Service;
